import { User, Mail, Shield } from "lucide-react";
import { useAuth } from "@/features/auth/index.js";
import { Avatar } from "@/shared/ui/atoms/Avatar.js";

const labelClass =
  "font-mono text-[0.625rem] font-medium uppercase tracking-[0.18em] text-muted-foreground";

interface Dato {
  icon: typeof User;
  label: string;
  value: string;
}

/**
 * Contenido de la página de perfil (solo lectura), SIN marco. Lo usa la ruta
 * /perfil (envuelto en AccountShell) y el AdminPanel como vista interna, así el
 * riel no cambia entre paneles: solo se reemplaza el contenido.
 */
export function PerfilContent({ onVerManual }: { onVerManual?: () => void }) {
  const { user } = useAuth();
  const rolLabel = user?.rol === "admin" ? "Administrador" : "Técnico";

  const datos: Dato[] = [
    { icon: User, label: "Nombre", value: user?.nombre ?? "—" },
    { icon: Mail, label: "Correo", value: user?.email ?? "—" },
    { icon: Shield, label: "Rol", value: rolLabel },
  ];

  return (
    <div className="forja-rise space-y-8">
      <div className="flex items-center gap-4">
        <Avatar name={user?.nombre ?? "?"} className="size-16 rounded-2xl text-base" />
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold text-foreground">
            {user?.nombre ?? "Mi perfil"}
          </h1>
          <p className={labelClass}>{rolLabel}</p>
        </div>
      </div>

      <dl className="grid gap-3 sm:grid-cols-2">
        {datos.map(({ icon: Icon, label, value }) => (
          <div key={label} className="rounded-2xl border border-border bg-card p-4">
            <dt className="flex items-center gap-2">
              <Icon className="size-4 text-muted-foreground" aria-hidden />
              <span className={labelClass}>{label}</span>
            </dt>
            <dd className="mt-2 truncate text-base text-foreground">{value}</dd>
          </div>
        ))}
      </dl>

      <p className="text-sm text-muted-foreground">
        Para cambiar tu nombre, correo o contraseña, contacta a un administrador.
      </p>

      {onVerManual && (
        <button
          type="button"
          onClick={onVerManual}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-foreground transition-colors hover:bg-secondary/60"
        >
          Ver manual de usuario
        </button>
      )}
    </div>
  );
}
